import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, Phone, Wrench, Car, MapPin, Clock,
  ShieldCheck, ArrowRight, ChevronDown, BadgeDollarSign,
} from "lucide-react";

const YEARS = Array.from({ length: 36 }, (_, i) => 2025 - i);

const PART_FORM_INIT = {
  name: "", email: "", phone: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "",
  part_needed: "", notes: "",
};

const VEHICLE_FORM_INIT = {
  name: "", email: "", phone: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "",
  vehicle_condition: "", vehicle_km: "", vehicle_location: "",
  asking_price: "", notes: "",
};

// ─── Dynamic meta tags per active tab ────────────────────────────────────
function useMeta(activeTab) {
  useEffect(() => {
    if (activeTab === "part") {
      document.title = "Request a Car Part Quote | BCP Brisbane Car Parts";
      const d = document.querySelector('meta[name="description"]');
      if (d) d.setAttribute("content",
        "Request a free quote for used or aftermarket car parts in Brisbane. Fast 24-hour response. BCP Brisbane Car Parts serves all QLD suburbs including Ipswich, Gold Coast, Sunshine Coast, Toowoomba and more.");
      const k = document.querySelector('meta[name="keywords"]');
      if (k) k.setAttribute("content",
        "car parts quote Brisbane, used car parts QLD, aftermarket parts Brisbane, request auto part, Brisbane wreckers quote, Toyota parts Brisbane, Ford parts Brisbane");
    } else {
      document.title = "Sell My Car Brisbane | Free Car Removal | BCP Brisbane Car Parts";
      const d = document.querySelector('meta[name="description"]');
      if (d) d.setAttribute("content",
        "Sell your car in Brisbane for cash. Free car removal and collection across Brisbane, Ipswich, Gold Coast, Toowoomba and all QLD. BCP Brisbane Car Parts buys all makes, models and conditions including non-runners.");
      const k = document.querySelector('meta[name="keywords"]');
      if (k) k.setAttribute("content",
        "sell my car Brisbane, free car removal Brisbane, cash for cars Brisbane, car removal QLD, scrap car Brisbane, sell used car Brisbane, car wreckers Brisbane buy cars");
    }
  }, [activeTab]);
}

export default function Quote() {
  const [activeTab, setActiveTab] = useState("part");
  const [partForm,    setPartForm]    = useState(PART_FORM_INIT);
  const [vehicleForm, setVehicleForm] = useState(VEHICLE_FORM_INIT);
  const [submitted, setSubmitted] = useState(null);
  const [loading,   setLoading]   = useState(false);

  useMeta(activeTab);

  // Reset submitted state when switching tabs
  const switchTab = (tab) => { setActiveTab(tab); setSubmitted(null); };

  // ── Submit part quote ─────────────────────────────────────────────────
  const submitPart = async (e) => {
    e.preventDefault();
    if (!partForm.name || !partForm.email || !partForm.part_needed) {
      toast.error("Please fill your name, email and the part you need"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", { ...partForm, quote_type: "part" });
      setSubmitted(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setLoading(false); }
  };

  // ── Submit vehicle quote ──────────────────────────────────────────────
  const submitVehicle = async (e) => {
    e.preventDefault();
    if (!vehicleForm.name || !vehicleForm.email || !vehicleForm.vehicle_make) {
      toast.error("Please fill your name, email and vehicle make"); return;
    }
    setLoading(true);
    try {
      const payload = {
        ...vehicleForm,
        quote_type: "vehicle",
        part_needed: `VEHICLE SALE/REMOVAL — ${vehicleForm.vehicle_year} ${vehicleForm.vehicle_make} ${vehicleForm.vehicle_model}`.trim(),
      };
      const { data } = await api.post("/quotes", payload);
      setSubmitted(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="quote-page">
      <StoreHeader />

      {/* ── Hero banner ─────────────────────────────────────────────── */}
      <div className="bg-[#0b1a3b] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16">

          {/* Toggle pill */}
          <div className="inline-flex bg-white/8 border border-white/15 rounded-sm p-1 mb-8">
            <button
              onClick={() => switchTab("part")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-[13px] font-heading font-semibold uppercase tracking-wide transition-all duration-200 ${
                activeTab === "part"
                  ? "bg-[#e31e24] text-white shadow-lg"
                  : "text-white/60 hover:text-white"
              }`}>
              <Wrench className="h-4 w-4" /> Request a Part
            </button>
            <button
              onClick={() => switchTab("vehicle")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-[13px] font-heading font-semibold uppercase tracking-wide transition-all duration-200 ${
                activeTab === "vehicle"
                  ? "bg-[#e31e24] text-white shadow-lg"
                  : "text-white/60 hover:text-white"
              }`}>
              <Car className="h-4 w-4" /> Sell My Car
            </button>
          </div>

          {activeTab === "part" ? (
            <>
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Can't find the part?</div>
              <h1 className="font-heading text-3xl md:text-[42px] font-bold text-white leading-tight mb-3">
                Request a Part Quote
              </h1>
              <p className="text-blue-200 text-[15px] max-w-xl leading-relaxed">
                Tell us the part you need and we'll source it from across Brisbane and Australia. Most quotes come back within 24 hours.
              </p>
            </>
          ) : (
            <>
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Free car removal Brisbane</div>
              <h1 className="font-heading text-3xl md:text-[42px] font-bold text-white leading-tight mb-3">
                Sell Your Car — Free Removal
              </h1>
              <p className="text-blue-200 text-[15px] max-w-xl leading-relaxed">
                We buy all makes, models and conditions across Brisbane and QLD — running or not. Get a fast cash offer and free pickup from your door.
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* ── Form column ────────────────────────────────────────── */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="border border-green-200 bg-green-50 rounded-sm p-10 text-center" data-testid="quote-submitted">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <div className="font-heading text-2xl font-bold text-[#0b1a3b] mb-2">
                  {activeTab === "part" ? "Quote Request Received!" : "Vehicle Enquiry Received!"}
                </div>
                <p className="text-zinc-600 text-[15px] mb-1">
                  Reference: <span className="font-mono font-bold text-[#0b1a3b]">{submitted.quote_number}</span>
                </p>
                <p className="text-sm text-zinc-500 mt-2">
                  Our team will be in touch at <strong>{submitted.email}</strong> within 24 hours.
                  {activeTab === "part" ? (
                    <><br />Urgent? Call <a href="tel:0738751024" className="text-[#e31e24] font-bold">07 3875 1024</a>.</>
                  ) : (
                    <><br />Urgent? Call <a href="tel:0401213250" className="text-[#e31e24] font-bold">0401 213 250</a>.</>
                  )}
                </p>
                <button
                  onClick={() => { setSubmitted(null); }}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-300 text-zinc-600 text-sm font-medium hover:border-[#0b1a3b] hover:text-[#0b1a3b] transition-colors rounded-sm">
                  Submit another request
                </button>
              </div>

            ) : activeTab === "part" ? (

              /* ── Part request form ─────────────────────────────── */
              <form onSubmit={submitPart} className="space-y-5" data-testid="quote-form">
                <div className="pb-2 border-b border-zinc-100">
                  <h2 className="font-heading font-bold text-[18px] text-[#0b1a3b]">Your Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Full Name *</Label>
                    <Input value={partForm.name} onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                      placeholder="John Smith" className="rounded-sm h-11" data-testid="quote-name" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Email Address *</Label>
                    <Input type="email" value={partForm.email} onChange={(e) => setPartForm({ ...partForm, email: e.target.value })}
                      placeholder="john@example.com" className="rounded-sm h-11" data-testid="quote-email" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Phone Number</Label>
                    <Input value={partForm.phone} onChange={(e) => setPartForm({ ...partForm, phone: e.target.value })}
                      placeholder="04xx xxx xxx" className="rounded-sm h-11" data-testid="quote-phone" />
                  </div>
                </div>

                <div className="pt-2 pb-2 border-b border-zinc-100">
                  <h2 className="font-heading font-bold text-[18px] text-[#0b1a3b]">Vehicle Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Make</Label>
                    <Input value={partForm.vehicle_make} onChange={(e) => setPartForm({ ...partForm, vehicle_make: e.target.value })}
                      placeholder="e.g. Toyota" className="rounded-sm h-11" data-testid="quote-make" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Model</Label>
                    <Input value={partForm.vehicle_model} onChange={(e) => setPartForm({ ...partForm, vehicle_model: e.target.value })}
                      placeholder="e.g. Hilux" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Year</Label>
                    <Select value={partForm.vehicle_year} onValueChange={(v) => setPartForm({ ...partForm, vehicle_year: v })}>
                      <SelectTrigger className="rounded-sm h-11"><SelectValue placeholder="Select year" /></SelectTrigger>
                      <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-2 pb-2 border-b border-zinc-100">
                  <h2 className="font-heading font-bold text-[18px] text-[#0b1a3b]">Part Required</h2>
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Part Needed *</Label>
                  <Input value={partForm.part_needed} onChange={(e) => setPartForm({ ...partForm, part_needed: e.target.value })}
                    placeholder="e.g. Front left headlight assembly, Engine 1KZ-TE, Gearbox auto…"
                    className="rounded-sm h-11" data-testid="quote-part" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Extra Notes</Label>
                  <textarea value={partForm.notes} onChange={(e) => setPartForm({ ...partForm, notes: e.target.value })}
                    rows={3} placeholder="OEM number, VIN, condition preference, any other details…"
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-[14px] text-[#0b1a3b] focus:outline-none focus:ring-2 focus:ring-[#1e5bb8] placeholder:text-zinc-400 resize-none" />
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Button type="submit" disabled={loading}
                    className="h-12 px-8 bcp-gradient-red text-white font-heading font-bold uppercase tracking-wide rounded-sm text-[14px]"
                    data-testid="quote-submit">
                    {loading ? "Submitting…" : "Submit Quote Request"} {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
                  </Button>
                  <div className="text-[13px] text-zinc-500 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-[#e31e24] shrink-0" />
                    Or call <a href="tel:0738751024" className="text-[#0b1a3b] font-bold hover:text-[#e31e24] transition-colors">07 3875 1024</a>
                  </div>
                </div>
              </form>

            ) : (

              /* ── Vehicle / sell my car form ────────────────────── */
              <form onSubmit={submitVehicle} className="space-y-5" data-testid="vehicle-quote-form">
                <div className="pb-2 border-b border-zinc-100">
                  <h2 className="font-heading font-bold text-[18px] text-[#0b1a3b]">Your Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Full Name *</Label>
                    <Input value={vehicleForm.name} onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })}
                      placeholder="John Smith" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Email Address *</Label>
                    <Input type="email" value={vehicleForm.email} onChange={(e) => setVehicleForm({ ...vehicleForm, email: e.target.value })}
                      placeholder="john@example.com" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Phone Number</Label>
                    <Input value={vehicleForm.phone} onChange={(e) => setVehicleForm({ ...vehicleForm, phone: e.target.value })}
                      placeholder="04xx xxx xxx" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Your Location / Suburb</Label>
                    <Input value={vehicleForm.vehicle_location} onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_location: e.target.value })}
                      placeholder="e.g. Ipswich, Gold Coast…" className="rounded-sm h-11" />
                  </div>
                </div>

                <div className="pt-2 pb-2 border-b border-zinc-100">
                  <h2 className="font-heading font-bold text-[18px] text-[#0b1a3b]">Vehicle Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Make *</Label>
                    <Input value={vehicleForm.vehicle_make} onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_make: e.target.value })}
                      placeholder="e.g. Toyota" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Model</Label>
                    <Input value={vehicleForm.vehicle_model} onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_model: e.target.value })}
                      placeholder="e.g. Hilux" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Year</Label>
                    <Select value={vehicleForm.vehicle_year} onValueChange={(v) => setVehicleForm({ ...vehicleForm, vehicle_year: v })}>
                      <SelectTrigger className="rounded-sm h-11"><SelectValue placeholder="Select year" /></SelectTrigger>
                      <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Condition</Label>
                    <Select value={vehicleForm.vehicle_condition} onValueChange={(v) => setVehicleForm({ ...vehicleForm, vehicle_condition: v })}>
                      <SelectTrigger className="rounded-sm h-11"><SelectValue placeholder="Select condition" /></SelectTrigger>
                      <SelectContent>
                        {["Excellent","Good","Fair","Poor","Non-Runner / Damaged","Written Off"].map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Odometer (km)</Label>
                    <Input value={vehicleForm.vehicle_km} onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_km: e.target.value })}
                      placeholder="e.g. 180,000" className="rounded-sm h-11" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Asking Price</Label>
                    <Input value={vehicleForm.asking_price} onChange={(e) => setVehicleForm({ ...vehicleForm, asking_price: e.target.value })}
                      placeholder="e.g. $3,500 or Best Offer" className="rounded-sm h-11" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mb-1.5 block">Extra Notes</Label>
                  <textarea value={vehicleForm.notes} onChange={(e) => setVehicleForm({ ...vehicleForm, notes: e.target.value })}
                    rows={3} placeholder="Any damage, defects, registration status, or extra info that helps us assess the vehicle…"
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-[14px] text-[#0b1a3b] focus:outline-none focus:ring-2 focus:ring-[#1e5bb8] placeholder:text-zinc-400 resize-none" />
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Button type="submit" disabled={loading}
                    className="h-12 px-8 bcp-gradient-red text-white font-heading font-bold uppercase tracking-wide rounded-sm text-[14px]"
                    data-testid="vehicle-quote-submit">
                    {loading ? "Submitting…" : "Get My Cash Offer"} {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
                  </Button>
                  <div className="text-[13px] text-zinc-500 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-[#e31e24] shrink-0" />
                    Or call <a href="tel:0401213250" className="text-[#0b1a3b] font-bold hover:text-[#e31e24] transition-colors">0401 213 250</a>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* ── Info sidebar ───────────────────────────────────────── */}
          <div className="space-y-4">
            {activeTab === "part" ? (
              <>
                <div className="bg-[#0b1a3b] rounded-sm p-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#e31e24] mb-3">Why choose BCP?</div>
                  {[
                    { icon:Clock,        text:"Quote back within 24 hours" },
                    { icon:ShieldCheck,  text:"90-day warranty on most parts" },
                    { icon:MapPin,       text:"Brisbane local — pickup available" },
                    { icon:Phone,        text:"Expert advice on 07 3875 1024" },
                  ].map(({ icon:Icon, text }) => (
                    <div key={text} className="flex items-start gap-3 mb-3 last:mb-0">
                      <Icon className="h-4 w-4 text-[#e31e24] shrink-0 mt-0.5" />
                      <span className="text-[13px] text-blue-200 leading-snug">{text}</span>
                    </div>
                  ))}
                </div>
                <div className="border border-zinc-200 rounded-sm p-5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">We source for</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["Toyota","Ford","Nissan","Honda","Subaru","Mitsubishi","Hyundai","Mazda","Lexus","Kia","Isuzu","Range Rover"].map((m) => (
                      <span key={m} className="text-[11px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-sm font-medium">{m}</span>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="bg-[#0b1a3b] rounded-sm p-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#e31e24] mb-3">How it works</div>
                  {[
                    { n:"1", text:"Submit your vehicle details above" },
                    { n:"2", text:"We'll call with a cash offer within hours" },
                    { n:"3", text:"Free pickup from your door across Brisbane & QLD" },
                    { n:"4", text:"Cash paid on the spot" },
                  ].map(({ n, text }) => (
                    <div key={n} className="flex items-start gap-3 mb-3 last:mb-0">
                      <div className="w-5 h-5 rounded-full bg-[#e31e24] flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] font-bold text-white">{n}</span>
                      </div>
                      <span className="text-[13px] text-blue-200 leading-snug">{text}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-green-50 border border-green-200 rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <BadgeDollarSign className="h-5 w-5 text-green-700" />
                    <span className="font-heading font-bold text-[14px] text-green-800">We Buy All Conditions</span>
                  </div>
                  <p className="text-[13px] text-green-700 leading-relaxed">Running, non-running, accident damaged, flood damaged, written-off — we'll make an offer on any vehicle across Brisbane and QLD.</p>
                </div>
                <div className="border border-zinc-200 rounded-sm p-5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Service areas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["Brisbane","Ipswich","Gold Coast","Sunshine Coast","Toowoomba","Caboolture","Logan","Redcliffe","Springfield","Beenleigh"].map((s) => (
                      <span key={s} className="text-[11px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-sm">{s}</span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── SEO Blog section ─────────────────────────────────────── */}
        <div className="mt-20 pt-12 border-t border-zinc-100">
          {activeTab === "part" ? (
            <div className="prose prose-zinc max-w-none">
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-3">Helpful information</div>
              <h2 className="font-heading text-[26px] font-bold text-[#0b1a3b] mb-6">
                Finding Used & Aftermarket Car Parts in Brisbane
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[14px] text-zinc-600 leading-relaxed">
                <div>
                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mb-2">How quickly will I get a quote?</h3>
                  <p>Most part quote requests submitted through our website are responded to within 24 hours, often the same business day. For urgent parts, we recommend calling our Brisbane team directly on 07 3875 1024 — we can often confirm stock on the spot.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">Do you source parts you don't have in stock?</h3>
                  <p>Yes. BCP Brisbane Car Parts is connected to a nationwide network of wreckers, importers, and parts suppliers across Australia. If we don't have it in our Richland yard, we'll find it for you — usually within 48 hours.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">What information helps you find my part faster?</h3>
                  <p>The more detail the better. Include your vehicle's Make, Model, Year, and if possible the OEM part number or VIN. This lets us match the exact part to your specific variant and confirm compatibility before quoting.</p>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mb-2">What vehicles do you specialise in?</h3>
                  <p>We stock parts for a wide range of Japanese, Korean, American and European vehicles. Our strongest inventory covers Toyota (Hilux, Landcruiser, Camry, RAV4), Ford (Ranger, Territory, Falcon), Nissan (Navara, Patrol, X-Trail), Honda, Subaru, Mitsubishi, Hyundai, Mazda, Kia, Lexus, Isuzu and Range Rover.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">Do used parts come with a warranty?</h3>
                  <p>Most of our used parts come with a 90-day warranty. Mechanical parts such as engines and gearboxes are tested before dispatch. If a part is faulty within the warranty period, we'll replace or refund it — no runaround.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">Can I pick up from your Brisbane yard?</h3>
                  <p>Absolutely. We're located in Richland, Brisbane (QLD 4077). You're welcome to pick up parts directly from our yard during business hours Monday–Friday 8am–5:30pm and Saturday 9am–1pm. Call ahead to confirm the part is ready.</p>
                </div>
              </div>

              {/* Schema: FAQ structured data */}
              <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  { "@type":"Question", "name":"How quickly will I get a car parts quote in Brisbane?",
                    "acceptedAnswer":{ "@type":"Answer","text":"Most quotes are responded to within 24 hours, often the same day. Call 07 3875 1024 for urgent parts." } },
                  { "@type":"Question", "name":"Do BCP Brisbane Car Parts source parts not in stock?",
                    "acceptedAnswer":{ "@type":"Answer","text":"Yes. We source from a nationwide network of wreckers and importers across Australia, usually within 48 hours." } },
                  { "@type":"Question", "name":"Do used car parts from BCP come with a warranty?",
                    "acceptedAnswer":{ "@type":"Answer","text":"Most used parts come with a 90-day warranty. Engines and gearboxes are tested before dispatch." } },
                ]
              })}} />
            </div>
          ) : (
            <div className="prose prose-zinc max-w-none">
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-3">Sell your car in Brisbane</div>
              <h2 className="font-heading text-[26px] font-bold text-[#0b1a3b] mb-6">
                Free Car Removal Brisbane — Cash for Cars Across QLD
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[14px] text-zinc-600 leading-relaxed">
                <div>
                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mb-2">How does free car removal work in Brisbane?</h3>
                  <p>It's simple. Submit your vehicle details above, and our team will contact you — usually within a few hours — with a cash offer. If you accept, we arrange a free pickup from your home, workplace, or any location across Brisbane, Ipswich, Gold Coast, Sunshine Coast, Toowoomba or anywhere in QLD. Cash is paid on the spot at collection.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">Do you buy non-running or damaged cars?</h3>
                  <p>Yes. We buy all conditions — running, non-running, accident damaged, hail damaged, flood damaged, and written-off vehicles. Even if your car hasn't started in years or has been sitting in your driveway, submit an enquiry and we'll make an offer. We accept cars, utes, vans and 4x4s of any make or model.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">What documents do I need to sell my car?</h3>
                  <p>You'll need proof of ownership (your name on the registration papers or title) and a valid photo ID. If the vehicle is unregistered, we'll still buy it — just let us know when you submit the form. Our team will walk you through any paperwork on collection day.</p>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mb-2">Which areas in Brisbane do you service for car removal?</h3>
                  <p>We cover all of Greater Brisbane and surrounding regions including Ipswich, Logan, Redcliffe, Caboolture, Morayfield, Springfield, Beenleigh, and the Gold Coast. For regional QLD including Toowoomba, Sunshine Coast, and further north, contact us directly to discuss collection options.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">How much will you pay for my car?</h3>
                  <p>The price depends on the make, model, year, condition and current parts demand. Because we're a working wrecker, we assess the value of the vehicle's usable parts — often allowing us to pay more than scrap metal prices. Submit your details above for an accurate, no-obligation offer.</p>

                  <h3 className="font-heading font-bold text-[16px] text-[#0b1a3b] mt-5 mb-2">Why sell to BCP Brisbane Car Parts?</h3>
                  <p>We're a Brisbane-based auto wrecker operating from Richland (QLD 4077). We deal directly — no middlemen — which means faster decisions and better prices. Our team handles the entire process: valuation, paperwork, and free collection. Call 0401 213 250 or submit above for a fast, fair offer.</p>
                </div>
              </div>

              {/* Suburb coverage belt */}
              <div className="mt-8 p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">Car removal service areas</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Brisbane CBD","Ipswich","Logan","Redcliffe","Caboolture","Morayfield","Springfield","Beenleigh","Gold Coast",
                    "Sunshine Coast","Toowoomba","Richland","Inala","Oxley","Rocklea","Acacia Ridge","Darra","Wacol","Gailes",
                    "Goodna","Riverview","North Ipswich","Booval","Silkstone","Leichhardt","Bundamba","Raceview",
                  ].map((s) => (
                    <span key={s} className="text-[12px] text-zinc-600 bg-white border border-zinc-200 px-2.5 py-1 rounded-sm">{s}</span>
                  ))}
                </div>
              </div>

              {/* Schema: LocalBusiness + FAQPage */}
              <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": ["AutoPartsStore","LocalBusiness"],
                "name": "BCP Brisbane Car Parts",
                "description": "Brisbane auto wrecker offering free car removal, cash for cars, and used/aftermarket car parts across Brisbane and QLD.",
                "address": { "@type":"PostalAddress","streetAddress":"83 Teraba Street","addressLocality":"Richland","addressRegion":"QLD","postalCode":"4077","addressCountry":"AU" },
                "telephone": "+61406291986",
                "url": "https://bcpcarparts.com.au",
                "openingHoursSpecification": [
                  { "@type":"OpeningHoursSpecification","dayOfWeek":["Monday","Tuesday","Wednesday","Thursday","Friday"],"opens":"08:00","closes":"17:30" },
                  { "@type":"OpeningHoursSpecification","dayOfWeek":["Saturday"],"opens":"09:00","closes":"13:00" },
                ],
                "areaServed": ["Brisbane","Ipswich","Gold Coast","Sunshine Coast","Toowoomba","Queensland"],
                "serviceType": ["Car Parts","Auto Wrecking","Free Car Removal","Cash for Cars","Vehicle Dismantling"],
              })}} />
              <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  { "@type":"Question","name":"How does free car removal work in Brisbane?",
                    "acceptedAnswer":{ "@type":"Answer","text":"Submit your vehicle details. We contact you with a cash offer, then arrange free pickup from anywhere in Brisbane or QLD. Cash paid on collection." } },
                  { "@type":"Question","name":"Do you buy non-running or damaged cars in Brisbane?",
                    "acceptedAnswer":{ "@type":"Answer","text":"Yes. We buy all conditions including non-runners, accident damaged, flood damaged, and written-off vehicles across Brisbane and QLD." } },
                  { "@type":"Question","name":"Which areas in Brisbane do you offer free car removal?",
                    "acceptedAnswer":{ "@type":"Answer","text":"We cover all of Greater Brisbane, Ipswich, Logan, Gold Coast, Sunshine Coast, Toowoomba, Caboolture and surrounding QLD regions." } },
                ]
              })}} />
            </div>
          )}
        </div>
      </div>

      <StoreFooter />
    </div>
  );
}